import type {
  RequestSemantics,
  ResponseRequestContext,
} from './response-contract-error'

type HttpRequestHeaders = Record<string, unknown>

export const REQUEST_ID_HEADER = 'X-Request-Id'
export const REQUEST_SEMANTICS_HEADER = 'X-Request-Semantics'
export const RESPONSE_CONTRACT_HEADER = 'X-Response-Contract'
export const REQUEST_ACTION_HEADER = 'X-Request-Action'

let fallbackRequestId = 0

function nextRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  fallbackRequestId += 1
  return `response-request-${fallbackRequestId}`
}

function headerValue(headers: HttpRequestHeaders | undefined, name: string): string | undefined {
  const entry = Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === name.toLowerCase())
  return typeof entry?.[1] === 'string' ? entry[1] : undefined
}

export function createResponseRequestContext({
  semantics = 'page-request',
  operation,
  action = 'explicit',
}: {
  semantics?: RequestSemantics
  operation?: string
  action?: ResponseRequestContext['action']
} = {}): {
  context: ResponseRequestContext
  headers: Record<string, string>
} {
  const id = nextRequestId()
  return {
    context: { id, semantics, operation, action },
    headers: {
      [REQUEST_ID_HEADER]: id,
      [REQUEST_SEMANTICS_HEADER]: semantics,
      [REQUEST_ACTION_HEADER]: action,
      ...(operation ? { [RESPONSE_CONTRACT_HEADER]: operation } : {}),
    },
  }
}

export function responseRequestContextFromHeaders(
  headers: HttpRequestHeaders | undefined,
  fallbackUrl: string,
): ResponseRequestContext {
  const semanticsHeader = headerValue(headers, REQUEST_SEMANTICS_HEADER)
  const semantics: RequestSemantics = semanticsHeader === 'form-request'
    ? 'form-request'
    : 'page-request'
  const purpose = headerValue(headers, 'Purpose')
  const actionHeader = headerValue(headers, REQUEST_ACTION_HEADER)

  return {
    id: headerValue(headers, REQUEST_ID_HEADER) ?? nextRequestId(),
    semantics,
    operation: headerValue(headers, RESPONSE_CONTRACT_HEADER),
    action: purpose === 'prefetch' || actionHeader === 'background' ? 'background' : 'explicit',
    url: fallbackUrl,
  }
}

export function requestHeader(
  headers: HttpRequestHeaders | undefined,
  name: string,
): string | undefined {
  return headerValue(headers, name)
}
